import {
  Classes,
  FormGroup,
  FormGroupFeedback,
  IFormGroupProps,
  IMultiSelectProps,
  Intent,
  ISelectOption,
  MultiSelect,
} from '@pinpointhq/thumbtack';
import { FieldHelperProps, FieldInputProps, FieldMetaProps, useField } from 'formik';
import * as React from 'react';

interface ISelectField
  extends Omit<IMultiSelectProps, 'activeItem' | 'intent' | 'buttonProps' | 'fill' | 'onItemSelect'> {
  minimal?: boolean;
  name: string;
  formGroupProps?: Omit<IFormGroupProps, 'labelFor'>;
  resetOnSelect?: boolean;
  throwError?: boolean;
}

export default function MultiSelectField<T extends string[]>({
  name,
  formGroupProps,
  resetOnSelect = true,
  throwError = true,
  minimal = false,
  ...selectProps
}: ISelectField) {
  const { items, handleChange } = selectProps;
  const [field, meta, helpers]: [FieldInputProps<T>, FieldMetaProps<T>, FieldHelperProps<T>] = useField<T>(name);
  const hasError = meta.touched && meta.error;

  const selectedItems = items.filter((item) => {
    if (typeof field.value === 'undefined' && throwError) {
      throw new Error('Field value is not yet defined - make sure Formik value is initialized');
    }
    return (field.value ?? []).includes(item.value);
  });

  const handleSelectChange = (options: ISelectOption[]) => {
    if (handleChange) {
      handleChange(options);
    }
    helpers.setValue(options.map((option) => option.value) as T);
  };

  return (
    <FormGroup {...formGroupProps} labelFor={name}>
      <MultiSelect
        {...selectProps}
        selectedItems={selectedItems && selectedItems}
        fill={true}
        items={items}
        handleChange={handleSelectChange}
        resetOnSelect={resetOnSelect}
        tagInputProps={minimal ? { tagProps: { minimal: true, className: Classes.MUTED } } : null}
      />
      {hasError && <FormGroupFeedback intent={Intent.DANGER}>{meta.error}</FormGroupFeedback>}
      <input type="hidden" name={field.name} value={field.value} />
    </FormGroup>
  );
}
